import { useSyncExternalStore } from 'react';

/**
 * 🔴 THE bn/en SCAFFOLD — ERP Phase 1a increment 5.
 *
 * §1a's third exit criterion is one sentence: *"the Bangla toggle **re-renders** the shell's
 * chrome"*. ⚠️ `re-renders` is the word that matters. A toggle that stores a preference and asks for
 * a reload satisfies a looser reading and not this one, which is why the store below is a real
 * subscribable one rather than a value read once at start-up.
 *
 * ## ⚠️ IT IS A SCAFFOLD, AND THE WORD IS THE ROADMAP'S OWN
 *
 * The keys exist, the CHROME is translated, and the screens stay English. 🔴 That is assumption A5
 * in `docs/ERP_PHASE1A_PLAN.md`, and the reason is not laziness: the nav reads *Invoices*, *Bills*,
 * *Landed costs*, *Blanket orders* — **Bangladeshi accounting terms of art**, where a plausible
 * guess is worse than English. A shopkeeper who reads an invented word for *landed cost* does not
 * think *"odd translation"*; they think the software means something else. 🔬 Supplying real copy
 * fills these same keys with no code change.
 *
 * ## 🔴 AND THE BANGLA BELOW HAS NOT BEEN READ BY A NATIVE SPEAKER
 *
 * ⚠️ **Recorded as a decision for the owner, not presented as finished.** These are ordinary
 * interface words rather than accounting ones, which is why they are here at all — but *"I am fairly
 * confident"* is not the standard for text a customer reads in their own language. `docs/HANDOVER.md`
 * carries it as **W13**.
 */

export type Lang = 'en' | 'bn';

/**
 * 🔬 THE KEYS ARE THE ENGLISH, NOT AN ABSTRACTION OVER IT.
 *
 * `t('Sign out')` rather than `t('shell.signOut')`. ⚠️ A key that is not the text needs a second
 * lookup to know what a screen says, and the failure mode of a missing translation becomes a raw
 * key — `shell.signOut` — in front of a user. With the English as the key, a missing Bangla string
 * falls back to correct English, which is the right failure.
 */
const BANGLA = {
  'Sign out': 'সাইন আউট',
  'Choose a company': 'কোম্পানি বাছুন',
  'All companies': 'সব কোম্পানি',
  Summary: 'সারসংক্ষেপ',
  Sections: 'বিভাগ',
  // The 2026-09-25 redesign: the sidebar's departments and the search.
  Home: 'হোম',
  'Sales & customers': 'বিক্রয় ও গ্রাহক',
  Purchasing: 'ক্রয়',
  Inventory: 'মজুদ',
  'Money & bank': 'অর্থ ও ব্যাংক',
  Accounting: 'হিসাবরক্ষণ',
  Projects: 'প্রকল্প',
  'HR & payroll': 'মানবসম্পদ ও বেতন',
  'Reports & settings': 'রিপোর্ট ও সেটিংস',
  'Go to a section…': 'কোনো বিভাগে যান…',
  'Go to': 'যান',
  'Nothing matches that.': 'মিলছে এমন কিছু নেই।',
  // DS-11a2: the palette's first section (§1.4).
  Recent: 'সাম্প্রতিক',
  'Collapse menu': 'মেনু ছোট করুন',
  'Expand menu': 'মেনু বড় করুন',
  'Close menu': 'মেনু বন্ধ করুন',
  // DS-11a2 review: the full-screen palette's way out on a phone.
  Close: 'বন্ধ করুন',
  Menu: 'মেনু',
  // DS-11a3: the user menu (§1.5) and the theme (O-4).
  Account: 'অ্যাকাউন্ট',
  Settings: 'সেটিংস',
  Theme: 'থিম',
  System: 'সিস্টেম',
  Light: 'হালকা',
  Dark: 'গাঢ়',
  // DS-11b2: Help in the user menu and the signed-out frame's help line (WCAG 3.2.6).
  Help: 'সাহায্য',
  'Need help?': 'সাহায্য লাগবে?',
  'Ask an administrator of your company.': 'আপনার কোম্পানির অ্যাডমিনকে জিজ্ঞাসা করুন।',
  // DS-11a1: the skip link (§1.1).
  'Skip to content': 'মূল অংশে যান',
  // DS-11b3: the bar's offline pill (F530).
  'Offline — changes will not save until the connection returns': 'অফলাইন — সংযোগ ফিরে না আসা পর্যন্ত পরিবর্তন সংরক্ষণ হবে না',
  // DS-11b4: the bar's VAT period (F591) and What's new in the user menu (F600).
  'VAT period': 'ভ্যাট মেয়াদ',
  Open: 'খোলা',
  Locked: 'লক করা',
  "What's new": 'নতুন কী',
  New: 'নতুন',
  'Loading…': 'লোড হচ্ছে…',
  Workbook: 'Workbook',
} as const satisfies Readonly<Record<string, string>>;

/**
 * 🔴 A PHRASE IS ONE THE DICTIONARY HAS — design-system plan DS-4 (the compile-time parity the plan asks for).
 *
 * `t()` takes only these, so a new chrome string without its Bangla does not compile: the English fallback is for the
 * open labels below, never for a phrase somebody forgot. `scripts/design/i18n.mjs` checks the same in the release log.
 */
export type Phrase = keyof typeof BANGLA;

const KEY = 'workbook.lang';
const listeners = new Set<() => void>();

function read(): Lang {
  try {
    return localStorage.getItem(KEY) === 'bn' ? 'bn' : 'en';
  } catch {
    // Private mode, blocked storage, or a test environment without it. English is the default and
    // a language preference is not worth a thrown error on a page load.
    return 'en';
  }
}

let current: Lang = read();

/**
 * Per USER, in `localStorage` — assumption A4.
 *
 * 🔬 Not per company: a Bangla-reading bookkeeper who works for three companies wants Bangla in all
 * three, and the language a person reads is a fact about the person.
 */
export function setLang(next: Lang): void {
  if (next === current) return;
  current = next;
  try {
    localStorage.setItem(KEY, next);
  } catch {
    // The toggle still works for this session; only the memory of it is lost.
  }
  for (const listener of listeners) listener();
}

/** For `useSyncExternalStore`, so every component sees one language and re-renders together. */
export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function snapshot(): Lang {
  return current;
}

/**
 * 🔴 THE HOOK THAT MAKES THE CRITERION TRUE. `useSyncExternalStore` is what turns `setLang` into a
 * re-render of every subscriber — which is precisely what *"re-renders the shell's chrome"* asks
 * for, and what a module-level variable read once could never do.
 */
export function useLang(): Lang {
  return useSyncExternalStore(subscribe, snapshot, () => 'en' as Lang);
}

/**
 * Translate, falling back to the English that was passed in.
 *
 * ⚠️ IT TAKES THE LANGUAGE AS AN ARGUMENT rather than reading the module state. A component that
 * called a zero-argument `t()` would render the right words and **would not re-render when the
 * language changed**, because React has no way to know it read anything. Passing `lang` in means the
 * caller has subscribed via `useLang`, and the criterion holds by construction rather than by
 * everybody remembering.
 */
export function t(lang: Lang, english: Phrase): string {
  // `?? english`: a cast can still smuggle in a string the dictionary lacks; it shows English, never an empty label.
  return lang === 'en' ? english : ((BANGLA as Readonly<Record<string, string>>)[english] ?? english);
}

const has = (english: string): english is Phrase => Object.prototype.hasOwnProperty.call(BANGLA, english);

/**
 * An OPEN label — a screen's name in the nav and the palette — translated when the dictionary has it and English
 * otherwise. Those names are Bangladeshi accounting terms of art that stay English until a translator supplies them
 * (assumption A5 above); `langFor` says so to the page.
 */
export function tLabel(lang: Lang, english: string): string {
  return lang === 'bn' && has(english) ? BANGLA[english] : english;
}

/**
 * 🔴 WHICH LANGUAGE AN OPEN LABEL CAME OUT IN — DS-4 (WCAG 3.1.2). Inside Bangla chrome (`lang="bn"`), a label the
 * dictionary lacks is English and must say so, or a screen reader reads *Landed costs* with Bangla rules and the
 * Bangla type layer sets it a step larger than its English neighbours. `undefined` everywhere else: it inherits.
 */
export function langFor(lang: Lang, english: string): Lang | undefined {
  return lang === 'bn' && !has(english) ? 'en' : undefined;
}

/** What the toggle shows for the OTHER language, in that language's own script. */
export const LANG_LABEL: Readonly<Record<Lang, string>> = { en: 'English', bn: 'বাংলা' };
